// Anvil's well-known development keys (public knowledge; they hold only local test ETH).
// Account #0 deploys the protocol locally, so the app offers #1..#5.
import { createConnector } from '@wagmi/core';
import { createWalletClient, getAddress, http, numberToHex } from 'viem';
import type { EIP1193RequestFn, Hex } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import { chain, publicClient } from '../config/chain';
import { RPC_URL } from '../config/env';

const ANVIL_KEYS: readonly Hex[] = [
  '0x59c6995e998f97a5a0044966f0945389dc9e86dae88c7a8412f4603b6b78690d',
  '0x5de4111afa1a4b94908f83103eb1f1706367c2e68ca870fc3fb9a804cdab365a',
  '0x7c852118294e51e653712a81e05800f419141751be58f605c371e15141b007a6',
  '0x47e179ec197488593b187f80a00eb0da91f1b9d0b13f8733639f19c30a34926a',
  '0x8b3a350cf5c34c9194ca85829a2df0ec3153be0318b5e2d3348e872092edffba',
];

export interface DevAccount {
  index: number;
  connectorId: string;
  address: `0x${string}`;
}

export const DEV_CONNECTOR_TYPE = 'hashcurve-local-test-account';

export const devAccounts: DevAccount[] = ANVIL_KEYS.map((pk, i) => ({
  index: i + 1,
  connectorId: `local-test-account-${i + 1}`,
  address: privateKeyToAccount(pk).address,
}));

/**
 * A wagmi connector that signs locally with one anvil dev key. `getClient` hands wagmi a viem
 * wallet client with a local account, so transactions are signed in the page and sent raw.
 */
export function localTestAccount(index: number) {
  const pk = ANVIL_KEYS[index - 1];
  if (!pk) throw new Error(`No local test account #${index}`);
  const account = privateKeyToAccount(pk);
  const id = `local-test-account-${index}`;
  const disconnectedKey = `${id}.disconnected` as const;
  const walletClient = createWalletClient({ account, chain, transport: http(RPC_URL) });

  const request: EIP1193RequestFn = async ({ method, params }) => {
    switch (method) {
      case 'eth_accounts':
      case 'eth_requestAccounts':
        return [account.address] as never;
      case 'eth_chainId':
        return numberToHex(chain.id) as never;
      case 'personal_sign': {
        const [data] = params as [Hex, string];
        return (await account.signMessage({ message: { raw: data } })) as never;
      }
      case 'eth_sendTransaction': {
        const [tx] = params as [{ to?: Hex; data?: Hex; value?: Hex; gas?: Hex }];
        return (await walletClient.sendTransaction({
          to: tx.to,
          data: tx.data,
          value: tx.value ? BigInt(tx.value) : undefined,
          gas: tx.gas ? BigInt(tx.gas) : undefined,
        })) as never;
      }
      case 'wallet_switchEthereumChain':
        return null as never;
      default:
        return (await publicClient.request({ method, params } as never)) as never;
    }
  };
  const provider = { request, on() {}, removeListener() {} };

  return createConnector<typeof provider>((config) => ({
    id,
    name: `Local test account #${index}`,
    type: DEV_CONNECTOR_TYPE,
    async connect() {
      await config.storage?.removeItem(disconnectedKey);
      return { accounts: [getAddress(account.address)], chainId: chain.id } as never;
    },
    async disconnect() {
      await config.storage?.setItem(disconnectedKey, true);
    },
    async getAccounts() {
      return [getAddress(account.address)];
    },
    async getChainId() {
      return chain.id;
    },
    async getProvider() {
      return provider;
    },
    async getClient() {
      return walletClient;
    },
    async isAuthorized() {
      // reconnect() tries every connector; only restore the one used last.
      const recent = await config.storage?.getItem('recentConnectorId');
      const off = await config.storage?.getItem(disconnectedKey);
      return recent === id && !off;
    },
    onAccountsChanged() {},
    onChainChanged() {},
    onDisconnect() {
      config.emitter.emit('disconnect');
    },
  }));
}
